import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { publicApi, teacherApi, setToken, getToken, logout as doLogout, setTrustedDeviceToken } from "@/lib/api";
import { ArrowLeft, KeyRound, LogIn, UserPlus, LogOut } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

export default function TeacherLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  // Track whether the last login attempt failed because email wasn't verified.
  // Drives the inline "Wyślij ponownie link aktywacyjny" CTA below the form.
  const [needsActivation, setNeedsActivation] = useState(false);
  const [resendingActivation, setResendingActivation] = useState(false);
  // 2FA challenge state
  const [otpChallenge, setOtpChallenge] = useState(null); // {challenge_id}
  const [otpCode, setOtpCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const nav = useNavigate();

  // Detect any existing session — both sessionStorage and backend cookie
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!getToken()) return;
        const { data } = await teacherApi.get("/auth/me");
        if (!cancelled) setCurrentUser(data);
      } catch { /* stale token — ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const switchAccount = async () => {
    await doLogout();
    setCurrentUser(null);
    setEmail(""); setPassword("");
    toast.info("Wylogowano. Możesz zalogować się jako inny użytkownik.");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsActivation(false);
    try {
      // Always clear any prior session before logging in as someone else.
      if (currentUser) await doLogout();
      const { data } = await publicApi.post("/auth/login", { email, password });
      if (data.otp_required) {
        setOtpChallenge({ challenge_id: data.challenge_id });
        toast.info("Wysłaliśmy 6-cyfrowy kod na Twój e-mail.");
        return;
      }
      setToken(data.token);
      toast.success(`Zalogowano jako ${data.teacher.name}`);
      nav("/teacher", { replace: true });
    } catch (err) {
      const status = err.response?.status;
      const d = err.response?.data?.detail;
      const msg = typeof d === "string" ? d : "Błąd logowania";
      // 403 = email not verified — offer resend
      if (status === 403 && /Aktywuj konto|not verified|nie zweryfikowany|niezweryfikowany/i.test(msg)) {
        setNeedsActivation(true);
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const resendActivation = async () => {
    if (!email) { toast.error("Wpisz swój adres e-mail"); return; }
    setResendingActivation(true);
    try {
      const { data } = await publicApi.post("/auth/resend-verification", { email });
      toast.success(data.message || "Link aktywacyjny wysłany.");
      setNeedsActivation(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się wysłać linku.");
    } finally {
      setResendingActivation(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length < 6) { toast.error("Wpisz 6-cyfrowy kod"); return; }
    setLoading(true);
    try {
      const { data } = await publicApi.post("/auth/verify-otp", {
        challenge_id: otpChallenge.challenge_id,
        code: otpCode,
        trust_device: trustDevice,
      });
      setToken(data.token);
      if (trustDevice && data.trusted_device_token) {
        setTrustedDeviceToken(data.trusted_device_token);
      }
      toast.success(`Zalogowano jako ${data.teacher.name}`);
      nav("/teacher", { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Nieprawidłowy kod");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <header className="border-b-2 border-[#0F172A] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-bold" data-testid="back-home-link">
            <ArrowLeft className="w-4 h-4" strokeWidth={2.5} /><span>Wróć</span>
          </Link>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#64748B]">Logowanie nauczyciela</div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="inline-flex items-center gap-2 brut bg-[#FEF08A] px-3 py-1 rounded-md mb-6">
            <KeyRound className="w-3 h-3" strokeWidth={3} />
            <span className="text-xs tracking-[0.2em] uppercase font-bold">Konto Nauczyciela</span>
          </div>
          <h1 className="font-display font-black text-4xl mb-2">Zaloguj się</h1>
          <p className="text-[#334155] mb-8">Dostęp do panelu kartkówek z chemii.</p>

          {currentUser && (
            <div
              className="brut bg-[#FEF08A] rounded-md p-4 mb-6"
              data-testid="already-logged-in-banner"
            >
              <div className="font-bold text-sm">Jesteś już zalogowany jako <b>{currentUser.name}</b></div>
              <div className="text-xs text-[#334155] mt-1">{currentUser.email}</div>
              <div className="flex gap-2 mt-3">
                <Button
                  onClick={() => nav("/teacher", { replace: true })}
                  className="brut bg-[#0F172A] hover:bg-[#0F172A] text-white font-bold rounded-md h-9"
                  data-testid="goto-dashboard-btn"
                >
                  Przejdź do panelu
                </Button>
                <Button
                  onClick={switchAccount}
                  variant="outline"
                  className="brut bg-white border-2 border-[#0F172A] font-bold rounded-md h-9"
                  data-testid="switch-account-btn"
                >
                  <LogOut className="w-3 h-3 mr-1" strokeWidth={2.5} />Wyloguj
                </Button>
              </div>
            </div>
          )}

          <form onSubmit={otpChallenge ? submitOtp : submit} className="brut bg-white rounded-md p-6 space-y-4">
            {otpChallenge ? (
              <>
                <div className="brut bg-[#FEF08A] rounded-md p-3 text-sm" data-testid="otp-instructions">
                  Wysłaliśmy 6-cyfrowy kod logowania na Twój adres e-mail. Kod jest ważny 5 minut.
                </div>
                <div>
                  <Label htmlFor="otp" className="font-bold text-sm">Kod logowania (6 cyfr)</Label>
                  <Input
                    id="otp" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, ""))}
                    className="mt-2 brut-input border-2 border-[#0F172A] rounded-md bg-white h-12 font-mono tracking-[0.5em] text-center text-2xl"
                    placeholder="123456" autoFocus required
                    data-testid="otp-code-input"
                  />
                </div>
                <label className="flex items-start gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={trustDevice}
                    onChange={(e) => setTrustDevice(e.target.checked)}
                    className="w-4 h-4 brut border-2 border-[#0F172A] mt-0.5"
                    data-testid="trust-device-checkbox"
                  />
                  <span className="text-sm text-[#334155] leading-snug">
                    <b>Zaufaj temu urządzeniu na 60 dni.</b> Następne logowania
                    z tej przeglądarki pominą kod 2FA. Włącz tylko na własnym,
                    bezpiecznym komputerze.
                  </span>
                </label>
                <Button
                  type="submit" disabled={loading || otpCode.length < 6}
                  className="w-full brut brut-hover bg-[#0F172A] hover:bg-[#0F172A] text-white font-bold rounded-md h-12"
                  data-testid="otp-submit-btn"
                >
                  {loading ? "Sprawdzam..." : "Potwierdź kod"}
                </Button>
                <Button
                  type="button" variant="outline"
                  onClick={() => { setOtpChallenge(null); setOtpCode(""); }}
                  className="w-full brut bg-white border-2 border-[#0F172A] font-bold rounded-md h-10"
                  data-testid="otp-cancel-btn"
                >
                  Anuluj — wróć do logowania
                </Button>
              </>
            ) : (
            <>
            <div>
              <Label htmlFor="email" className="font-bold text-sm">Adres e-mail</Label>
              <Input
                id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 brut-input border-2 border-[#0F172A] rounded-md bg-white h-12"
                placeholder="nauczyciel@example.pl" autoFocus required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="font-bold text-sm">Hasło</Label>
              <PasswordInput
                id="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 brut-input border-2 border-[#0F172A] rounded-md bg-white h-12 font-mono px-3"
                placeholder="••••••••" required
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit" disabled={loading || !email || !password}
              className="w-full brut brut-hover bg-[#0F172A] hover:bg-[#0F172A] text-white font-bold rounded-md h-12"
              data-testid="login-submit-btn"
            >
              <LogIn className="w-4 h-4 mr-2" strokeWidth={2.5} />
              {loading ? "Loguję..." : "Zaloguj"}
            </Button>
            {needsActivation && (
              <div className="brut border-2 border-[#EAB308] bg-[#FEF9C3] rounded-md p-3 text-sm space-y-2" data-testid="activation-required-banner">
                <div className="font-bold">Konto nie zostało jeszcze aktywowane 📬</div>
                <p className="text-xs">
                  Sprawdź skrzynkę (także <b>SPAM</b>) — lub wyślij nowy link poniżej.
                  Link jest ważny 24h.
                </p>
                <Button
                  type="button"
                  onClick={resendActivation}
                  disabled={resendingActivation || !email}
                  className="w-full brut brut-hover bg-[#FEF08A] hover:bg-[#FEF08A] text-[#0F172A] border-2 border-[#0F172A] font-bold rounded-md text-xs h-9"
                  data-testid="resend-activation-btn"
                >
                  {resendingActivation ? "Wysyłam..." : "Wyślij ponownie link aktywacyjny"}
                </Button>
              </div>
            )}
            <div className="flex items-center justify-between pt-2">
              <Link to="/forgot-password" className="text-sm text-[#64748B] hover:text-[#0F172A] underline" data-testid="forgot-password-link">
                Zapomniałem hasła
              </Link>
              <Link
                to="/register"
                className="text-sm font-bold text-[#0F172A] inline-flex items-center gap-1 hover:underline"
                data-testid="goto-register-link"
              >
                <UserPlus className="w-3 h-3" strokeWidth={2.5} />Zarejestruj konto
              </Link>
            </div>
            </>
            )}
          </form>

          <div className="mt-4 text-xs text-[#64748B] text-center">
            Rejestrując konto, akceptujesz{" "}
            <Link to="/terms" className="underline">Regulamin</Link> i{" "}
            <Link to="/privacy" className="underline">Politykę prywatności</Link>.
          </div>
        </div>
      </div>
    </div>
  );
}
